import MyTable, { ColumnHeader, RowData } from "@/components/MyTable";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useDebouncedInput } from "@/hooks/useDebouncedInput";
import { useGetData } from "@/hooks/useGetData";
import {
  Building2,
  Cross,
  Search,
  X,
  Plus,
  ChevronsDown,
  ChevronsDownIcon,
  ChevronDown,
  Download,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import LoaderWrapper from "@/pages/LoaderWrapper";
import { Pagination } from "./Pagination";
import { Label } from "./ui/label";
import { MultiSelect } from "./ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import * as XLSX from "xlsx";
import { useGetSearchParams } from "@/hooks/useGetSearchParams";
import FilterDropdown from "./FilterDropDowns";
import clsx from "clsx";
import AssignMultiSelect from "./AssignMultiSelect";
import { isPermissionNavRole, useAuth } from "@/contexts/use-auth";
import AxiosInstance from "@/utils/AxiosInstance";
import { TableSkeleton } from "./ui/Skeleton/TableSkeleton";

const EMPTY_HIDDEN_COLUMN_IDS: string[] = [];
const EMPTY_PERSIST_FILTER_KEYS: string[] = [];

function pickPersistFilters(
  source: Record<string, any> | null | undefined,
  keys: string[],
): Record<string, any> {
  const next: Record<string, any> = {};
  keys.forEach((key) => {
    const value = source?.[key];
    next[key] = Array.isArray(value)
      ? value
      : value != null && value !== ""
        ? value
        : [];
  });
  return next;
}

/** Supports legacy flat `{ facilityIds }` and org-scoped `{ [orgId]: { facilityIds } }`. */
function normalizeScopedPreferenceFilters(
  rawFilters: Record<string, any> | null | undefined,
  keys: string[],
  scopeKey?: string | null,
): Record<string, any> {
  if (!rawFilters || typeof rawFilters !== "object") return {};

  const orgScopedEntries = Object.entries(rawFilters).filter(
    ([key, value]) =>
      !keys.includes(key) &&
      value &&
      typeof value === "object" &&
      !Array.isArray(value),
  );
  const hasLegacyFlat = keys.some((key) => key in rawFilters);

  // Prefer org-keyed map: { [orgId]: { facilityIds: string[] } }
  if (orgScopedEntries.length > 0) {
    const next = Object.fromEntries(orgScopedEntries);
    if (scopeKey && hasLegacyFlat) {
      next[scopeKey] = {
        ...(next[scopeKey] || {}),
        ...pickPersistFilters(rawFilters, keys),
      };
    }
    return next;
  }

  // Legacy flat: { facilityIds: string[] } → migrate under current org when available
  if (hasLegacyFlat) {
    if (scopeKey) {
      return { [scopeKey]: pickPersistFilters(rawFilters, keys) };
    }
    return pickPersistFilters(rawFilters, keys);
  }

  return {};
}

function getScopedPersistFilters(
  storedFilters: Record<string, any> | null | undefined,
  keys: string[],
  scopeKey?: string | null,
): Record<string, any> {
  if (!scopeKey) {
    return pickPersistFilters(storedFilters, keys);
  }
  const scoped = storedFilters?.[scopeKey];
  if (scoped && typeof scoped === "object" && !Array.isArray(scoped)) {
    return pickPersistFilters(scoped, keys);
  }
  return pickPersistFilters({}, keys);
}

type MoreActions = {
  label?: string;
  icon?: any;
  onClick?: (data: any) => void;
  isShow: (data?: any) => boolean;
  isDisabled?: (data?: any) => boolean;
  /** Custom row action (e.g. inline switch). Keeps menu open when used with `preventClose`. */
  render?: (data: any) => React.ReactNode;
  preventClose?: boolean;
};

export type TableCustomFetchArgs = {
  page: number;
  limit: number;
  search: string;
  params: Record<string, any>;
  filters: Record<string, any>;
};

export type TableCustomFetchResult = {
  data: any[];
  totalPages?: number;
  total?: number;
};
type Props = {
  heading: string;
  url: string;
  params: any;
  apiDataKey: string;
  fetchCondition: boolean;
  isIncludeSearch: boolean;
  searchPlaceholder: string;
  columnHeaders: ColumnHeader[];
  data: any;
  viewData: any;
  setData: any;
  isActionColumn: boolean;
  filtersOptions: any[];
  handleAction: (data: any) => void;
  handleEditAction?: (data: any) => void;
  filters: any;
  setFilters: (data: any) => void;
  clickRow: (data: any) => void;
  isColumnSelection?: boolean;
  isDownloadData?: boolean;
  /** e.g. `canAccessReports` — admin + managers with that `canAccess*` flag */
  downloadAccessModule?: string;
  moreActions?: MoreActions[];
  isSelectable?: boolean;
  selectActions?: any[];
  handleSearchActionsClicked?: (
    selected: any,
    key: any,
    value: any,
    cb: any,
  ) => void;
  donotFetch?: boolean;
  isIgnoreSearchParams?: boolean;
  clickCell?: any;
  defaultFilters?: Record<string, any>;
  isActionDisabled?: (row: any) => boolean;
  isActionsHidden?: (row: any) => boolean;
  /** When set, Download Data button uses this instead of client-side export */
  onDownload?: (ctx?: {
    search?: string;
    visibleColumnIds: string[];
    columnOrder: string[];
    visibleColumns: Record<string, boolean>;
    sort?: {
      sortKey?: string;
      sortType?: string;
    } | null;
  }) => void | Promise<void>;
  /** Optional key to persist table column order/visibility */
  columnPreferenceKey?: string;
  /** Column ids hidden by default when no saved preference exists */
  defaultHiddenColumnIds?: string[];
  /** Sent as `tableName` in table-preferences API; defaults to `columnPreferenceKey` */
  preferenceTableName?: string;
  /**
   * Filter keys to persist/restore via table-preferences `filters` payload.
   * Example: `["facilityIds"]`
   */
  persistFilterKeys?: string[];
  /**
   * When set, filters are stored per scope (e.g. org id):
   * `{ filters: { [orgId]: { facilityIds: string[] } } }`
   */
  persistFilterScopeKey?: string | null;
  /** Extra toolbar controls shown near filters/actions */
  headerExtraControls?: React.ReactNode;
  /** Extra toolbar controls shown beside Column View (e.g. download) */
  headerActions?: React.ReactNode;
  /** Merged onto the search input wrapper (e.g. narrower width on one page) */
  searchWrapperClassName?: string;
  /** Runs when user clears all filters from the filter dropdown */
  onClearAllFilters?: () => void;
  isHideClearAllFilters?: boolean;
  /**
   * If true, keep a single universal table skeleton while either:
   * - preferences are loading, or
   * - data is loading
   *
   * Default false to preserve existing loaders across the app.
   */
  universalLoadingSkeleton?: boolean;
  /**
   * With `donotFetch`, filter rows using the built-in search input (no API).
   * `data` and `viewData` must be the same length and row order.
   */
  clientSearchRowFilter?: (row: any, searchLower: string) => boolean;
  /**
   * When set, TableWithSearch uses this instead of `useGetData`.
   * UI page is 1-based; convert to the API's paging in the fetcher.
   */
  customFetch?: (args: TableCustomFetchArgs) => Promise<TableCustomFetchResult>;
};
const TableWithSearch = ({
  heading = "",
  url,
  params = {},
  fetchCondition = true,
  isIncludeSearch,
  searchPlaceholder,
  columnHeaders,
  apiDataKey,
  data,
  viewData,
  setData,
  isActionColumn,
  handleAction,
  handleEditAction,
  filtersOptions = [],
  filters,
  setFilters,
  clickRow,
  isColumnSelection = false,
  isDownloadData = false,
  downloadAccessModule,
  moreActions = [],
  isSelectable = false,
  selectActions = [],
  donotFetch = false,
  isIgnoreSearchParams = false,
  handleSearchActionsClicked = (a: any, b: any, c: any, cb: any) => {},
  clickCell = [],
  defaultFilters = {},
  isActionDisabled,
  isActionsHidden,
  onDownload,
  columnPreferenceKey,
  defaultHiddenColumnIds,
  preferenceTableName,
  persistFilterKeys,
  persistFilterScopeKey = null,
  headerExtraControls,
  headerActions,
  searchWrapperClassName,
  onClearAllFilters,
  isHideClearAllFilters = false,
  universalLoadingSkeleton = false,
  clientSearchRowFilter,
  customFetch,
}: Props) => {
  const persistFilterKeysList = persistFilterKeys ?? EMPTY_PERSIST_FILTER_KEYS;
  const persistFilterKeysKey = persistFilterKeysList.join("\0");
  const persistFilterScope = persistFilterScopeKey || null;
  const hiddenColumnIds = defaultHiddenColumnIds ?? EMPTY_HIDDEN_COLUMN_IDS;
  const hiddenColumnsKey = hiddenColumnIds.join("\0");
  const hiddenColumnIdSet = React.useMemo(
    () => new Set(hiddenColumnIds),
    [hiddenColumnsKey],
  );

  const { user, canAccess } = useAuth();
  const canDownload =
    !!isDownloadData &&
    (user?.role?.toLowerCase() === "admin" ||
      (!!downloadAccessModule &&
        isPermissionNavRole(user?.role) &&
        canAccess(downloadAccessModule)));

  const { input, setInput, debouncedInput } = useDebouncedInput("", 500);
  const [selected, setSelected] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [unselectedPatientIds, setUnselectedPatientIds] = useState<any[]>([]);

  const clearBulkSelection = () => {
    setSelected([]);
    setSelectAll(false);
    setUnselectedPatientIds([]);
  };

  const getBulkSelectionPayload = () => {
    if (selectAll) {
      return {
        selectAll: true,
        patientIds: [],
        unselectedPatientIds,
      };
    }
    return {
      selectAll: false,
      patientIds: selected,
      unselectedPatientIds: [],
    };
  };

  const hasBulkSelection =
    selectAll || (Array.isArray(selected) && selected.length > 0);

  const [page, setPage] = useState(1);
  // const [totalPages]
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState({
    sortKey: "",
    sortType: "",
    sortKeyMain: "",
  });
  // console.log({sort})
  // const [sort, setSort] = useState({ sortKey: "", sortType: "" , sortKeyMain: "" });
  // // console.log({sort})
  const [searchParams] = useGetSearchParams();
  const skip = (page - 1) * limit;

  const preferenceApiTableName =
    preferenceTableName ?? columnPreferenceKey ?? "patient-list";

  const filtersKey = JSON.stringify(filters ?? {});
  const paramsKey = JSON.stringify(params ?? {});
  // Transform filters: convert multi-select arrays to comma-separated strings
  const processedFilters = React.useMemo(() => {
    const processed = { ...filters };
    if (
      processed.caretakers &&
      Array.isArray(processed.caretakers) &&
      processed.caretakers.length > 0
    ) {
      processed.caretakers = processed.caretakers.join(",");
    } else if (
      processed.caretakers &&
      Array.isArray(processed.caretakers) &&
      processed.caretakers.length === 0
    ) {
      delete processed.caretakers;
    }
    if (
      processed.facilityIds &&
      Array.isArray(processed.facilityIds) &&
      processed.facilityIds.length > 0
    ) {
      processed.facilityIds = processed.facilityIds.join(",");
    } else if (
      processed.facilityIds &&
      Array.isArray(processed.facilityIds) &&
      processed.facilityIds.length === 0
    ) {
      delete processed.facilityIds;
    }
    // Remove patientType filter when "all" is selected (works like default - no filter)
    if (processed.patientType === "all" || processed.patientType === "") {
      delete processed.patientType;
    }
    return processed;
  }, [filtersKey]);

  const requestParams = React.useMemo(
    () => ({
      search: debouncedInput,
      ...params,
      page,
      limit,
      skip,
      ...(isIgnoreSearchParams ? {} : searchParams),
      ...(sort?.sortType !== ""
        ? {
            sortKey: sort.sortKeyMain !== "" ? sort.sortKeyMain : sort.sortKey,
            sortType: sort.sortType,
          }
        : {}),
      ...processedFilters,
    }),
    [
      debouncedInput,
      paramsKey,
      page,
      limit,
      skip,
      isIgnoreSearchParams,
      searchParams,
      sort.sortKey,
      sort.sortType,
      sort.sortKeyMain,
      processedFilters,
    ],
  );

  const setDataRef = useRef(setData);
  setDataRef.current = setData;

  const shouldWaitForPreferences = isColumnSelection && !!columnPreferenceKey;
  const shouldWaitForPreferenceFilters =
    shouldWaitForPreferences && persistFilterKeysList.length > 0;
  const [isPreferencesReady, setIsPreferencesReady] = useState(
    !shouldWaitForPreferences,
  );
  const [persistedFilters, setPersistedFilters] = useState<
    Record<string, any> | undefined
  >(() => (shouldWaitForPreferenceFilters ? undefined : {}));

  const useCustomFetch = typeof customFetch === "function";
  const customFetchRef = useRef(customFetch);
  customFetchRef.current = customFetch;
  const [customLoading, setCustomLoading] = useState(false);

  const { loading: hookLoading } = useGetData(
    url,
    requestParams,
    false,
    !useCustomFetch &&
      !donotFetch &&
      fetchCondition &&
      (!shouldWaitForPreferenceFilters || isPreferencesReady),
    (err, apidata) => {
      if (apidata && !err) {
        setDataRef.current(
          apiDataKey ? apidata?.data?.[apiDataKey] : apidata?.data,
        );
        const root = apidata?.data as Record<string, any> | undefined;
        const rawTotal =
          root?.total ?? root?.pagination?.total ?? root?.pagination?.count;
        const totalRecords = Number(rawTotal);
        const pageCount =
          Number.isFinite(totalRecords) && totalRecords >= 0
            ? Math.max(1, Math.ceil(totalRecords / limit))
            : 1;
        setTotalPages(pageCount);
      }
    },
  );

  useEffect(() => {
    if (!useCustomFetch || donotFetch) return;
    if (!fetchCondition) return;
    if (shouldWaitForPreferenceFilters && !isPreferencesReady) return;

    let cancelled = false;
    (async () => {
      setCustomLoading(true);
      try {
        const result = await customFetchRef.current?.({
          page,
          limit,
          search: debouncedInput,
          params,
          filters: processedFilters,
        });
        if (cancelled || !result) return;
        const rows = Array.isArray(result.data) ? result.data : [];
        setDataRef.current(rows);
        const totalRecords = Number(result.total);
        const fromTotal =
          Number.isFinite(totalRecords) && totalRecords >= 0
            ? Math.max(1, Math.ceil(totalRecords / limit))
            : undefined;
        const pageCount = Number(result.totalPages);
        setTotalPages(
          Number.isFinite(pageCount) && pageCount > 0
            ? pageCount
            : fromTotal || 1,
        );
      } catch (e) {
        if (cancelled) return;
        setDataRef.current([]);
        setTotalPages(1);
      } finally {
        if (!cancelled) setCustomLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    useCustomFetch,
    donotFetch,
    fetchCondition,
    shouldWaitForPreferenceFilters,
    isPreferencesReady,
    page,
    limit,
    debouncedInput,
    paramsKey,
    filtersKey,
  ]);

  const loading = useCustomFetch ? customLoading : hookLoading;

  const filteredPairs = React.useMemo(() => {
    const cols = (data || []) as any[];
    const viewsRaw = viewData as any[] | undefined;
    const views =
      Array.isArray(viewsRaw) && viewsRaw.length === cols.length
        ? viewsRaw
        : cols;
    const q = (debouncedInput || "").trim().toLowerCase();
    if (!donotFetch || !clientSearchRowFilter || !q) {
      return { cols, views };
    }
    const fc: any[] = [];
    const fv: any[] = [];
    cols.forEach((row, i) => {
      if (clientSearchRowFilter(row, q)) {
        fc.push(row);
        fv.push(views[i] ?? row);
      }
    });
    return { cols: fc, views: fv };
  }, [data, viewData, debouncedInput, donotFetch, clientSearchRowFilter]);

  const displayCols = React.useMemo(() => {
    if (!donotFetch) return filteredPairs.cols;
    const start = (page - 1) * limit;
    return filteredPairs.cols.slice(start, start + limit);
  }, [donotFetch, filteredPairs.cols, page, limit]);

  const displayViews = React.useMemo(() => {
    if (!donotFetch) return filteredPairs.views;
    const start = (page - 1) * limit;
    return filteredPairs.views.slice(start, start + limit);
  }, [donotFetch, filteredPairs.views, page, limit]);

  React.useEffect(() => {
    if (!donotFetch) return;
    const n = filteredPairs.cols.length;
    setTotalPages(Math.max(1, Math.ceil(n / limit)));
  }, [donotFetch, filteredPairs.cols.length, limit]);

  React.useEffect(() => {
    if (!donotFetch) return;
    const maxPage = Math.max(
      1,
      Math.ceil(filteredPairs.cols.length / limit) || 1,
    );
    if (page > maxPage) setPage(maxPage);
  }, [donotFetch, filteredPairs.cols.length, limit, page]);

  React.useEffect(() => {
    if (!donotFetch || !clientSearchRowFilter) return;
    setPage(1);
  }, [debouncedInput, donotFetch, clientSearchRowFilter]);

  // Reset bulk selection when list query context changes.
  React.useEffect(() => {
    clearBulkSelection();
  }, [filtersKey, paramsKey, debouncedInput]);

  const nav = useNavigate();

  // console.log(columnsData);

  const handleSort = (
    headerId: string,
    sortType: string,
    sortKey: string | null | undefined,
  ): void => {
    // console.log(`Sort ${headerId}:`, sortType);
    setSort({
      sortKey: headerId,
      sortType: sortType,
      sortKeyMain: sortKey ?? "",
    });
  };

  const getDefaultColumnVisibility = React.useCallback(
    (id: string) => !hiddenColumnIdSet.has(id),
    [hiddenColumnIdSet],
  );

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const initial: Record<string, boolean> = {};
    columnHeaders.forEach((header) => {
      initial[header.id] = !hiddenColumnIdSet.has(header.id);
    });
    return initial;
  });
  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    (columnHeaders || []).map((h) => h.id),
  );
  const isPreferencesLoadedRef = useRef(false);
  const loadedPreferencesKeyRef = useRef<string | null>(null);
  const loadingPreferencesKeyRef = useRef<string | null>(null);
  const lastAppliedScopeRef = useRef<string | null>(null);

  const lastSyncedPreferencesRef = useRef<string | null>(null);

  useEffect(() => {
    const ids = (columnHeaders || []).map((h) => h.id);
    setColumnOrder((prev) => {
      if (!prev || prev.length === 0) return ids;
      return [
        ...prev.filter((id) => ids.includes(id)),
        ...ids.filter((id) => !prev.includes(id)),
      ];
    });
  }, [columnHeaders]);

  const applyScopedFiltersToState = React.useCallback(
    (stored: Record<string, any>, scopeKey: string | null) => {
      if (persistFilterKeysList.length === 0) return;
      const scopedFilters = getScopedPersistFilters(
        stored,
        persistFilterKeysList,
        scopeKey,
      );
      setFilters((prev: any) => {
        const next = { ...(prev || {}) };
        persistFilterKeysList.forEach((key) => {
          if (
            Array.isArray(scopedFilters[key]) &&
            scopedFilters[key].length === 0
          ) {
            delete next[key];
          } else {
            next[key] = scopedFilters[key];
          }
        });
        return next;
      });
    },
    [persistFilterKeysList, setFilters],
  );

  useEffect(() => {
    if (!isColumnSelection || !columnPreferenceKey) return;

    // Scope key is applied after load; do not refetch prefs when only org changes
    const preferencesKey = `${columnPreferenceKey}:${preferenceApiTableName}:${hiddenColumnsKey}:${persistFilterKeysKey}`;
    if (loadedPreferencesKeyRef.current === preferencesKey) return;

    // Already requesting
    if (loadingPreferencesKeyRef.current === preferencesKey) {
      return;
    }
    loadingPreferencesKeyRef.current = preferencesKey;

    setIsPreferencesReady(false);
    isPreferencesLoadedRef.current = false;
    if (persistFilterKeysList.length > 0) {
      setPersistedFilters(undefined);
    }
    (async () => {
      let resolvedVisible: Record<string, boolean> | undefined;
      let resolvedOrder: string[] | undefined;
      let resolvedFilters: Record<string, any> | undefined;
      try {
        const response: any = await AxiosInstance().get(
          `/table-preferences/${columnPreferenceKey}`,
          { params: { tableName: preferenceApiTableName } },
        );
        const data = response?.data;
        const pref = data?.data || {};
        const apiVisible = pref?.visibleColumns;
        const apiOrder = pref?.columnOrder;
        const ids = (columnHeaders || []).map((h) => h.id);

        if (apiVisible && typeof apiVisible === "object") {
          const merged: Record<string, boolean> = {};
          const userCustomizedHiddenColumns = ids.some(
            (id) => hiddenColumnIdSet.has(id) && apiVisible[id] === false,
          );

          ids.forEach((id) => {
            if (hiddenColumnIdSet.has(id)) {
              merged[id] = userCustomizedHiddenColumns
                ? apiVisible[id] === true
                : getDefaultColumnVisibility(id);
              return;
            }

            merged[id] =
              typeof apiVisible[id] === "boolean" ? apiVisible[id] : true;
          });
          resolvedVisible = merged;
          setVisibleColumns((prev) => {
            const changed = ids.some((id) => prev[id] !== merged[id]);
            return changed ? merged : prev;
          });
        }

        if (Array.isArray(apiOrder) && apiOrder.length > 0) {
          const nextOrder = [
            ...apiOrder.filter((id: string) => ids.includes(id)),
            ...ids.filter((id) => !apiOrder.includes(id)),
          ];
          resolvedOrder = nextOrder;
          setColumnOrder((prev) => {
            const changed =
              prev.length !== nextOrder.length ||
              prev.some((id, index) => id !== nextOrder[index]);
            return changed ? nextOrder : prev;
          });
        }

        if (persistFilterKeysList.length > 0) {
          const loadedFilters = normalizeScopedPreferenceFilters(
            pref?.filters,
            persistFilterKeysList,
            persistFilterScope,
          );
          resolvedFilters = loadedFilters;
          setPersistedFilters(loadedFilters);
          lastAppliedScopeRef.current = persistFilterScope;
          applyScopedFiltersToState(loadedFilters, persistFilterScope);
        }
      } catch (error) {
        // Preferences are optional; ignore if API not implemented yet.
        if (persistFilterKeysList.length > 0) {
          const emptyFilters = persistFilterScope
            ? {}
            : pickPersistFilters({}, persistFilterKeysList);
          resolvedFilters = emptyFilters;
          setPersistedFilters(emptyFilters);
          lastAppliedScopeRef.current = persistFilterScope;
          applyScopedFiltersToState(emptyFilters, persistFilterScope);
        }
      } finally {
        loadedPreferencesKeyRef.current = preferencesKey;
        isPreferencesLoadedRef.current = true;
        lastSyncedPreferencesRef.current = JSON.stringify({
          columnOrder: resolvedOrder ?? columnOrder,
          visibleColumns: resolvedVisible ?? visibleColumns,
          ...(persistFilterKeysList.length > 0
            ? { filters: resolvedFilters ?? {} }
            : {}),
        });
        setIsPreferencesReady(true);
        // Request is no longer in-flight
        if (loadingPreferencesKeyRef.current === preferencesKey) {
          loadingPreferencesKeyRef.current = null;
        }
      }
    })();
  }, [
    isColumnSelection,
    columnPreferenceKey,
    columnHeaders,
    preferenceApiTableName,
    hiddenColumnsKey,
    persistFilterKeysKey,
    getDefaultColumnVisibility,
    applyScopedFiltersToState,
  ]);

  useEffect(() => {
    if (!shouldWaitForPreferences) {
      isPreferencesLoadedRef.current = true;
      setIsPreferencesReady(true);
      if (persistFilterKeysList.length > 0) {
        if (persistFilterScope) {
          setPersistedFilters({
            [persistFilterScope]: pickPersistFilters(
              filters,
              persistFilterKeysList,
            ),
          });
        } else {
          setPersistedFilters(
            pickPersistFilters(filters, persistFilterKeysList),
          );
        }
      }
    }
  }, [shouldWaitForPreferences, persistFilterKeysKey]);

  // When org (scope) changes after prefs are loaded, restore that org's facility filters
  useEffect(() => {
    if (!isPreferencesLoadedRef.current) return;
    if (persistFilterKeysList.length === 0) return;
    if (persistedFilters === undefined) return;

    if (!persistFilterScope) {
      if (lastAppliedScopeRef.current) {
        applyScopedFiltersToState({}, null);
        lastAppliedScopeRef.current = null;
      }
      return;
    }

    if (lastAppliedScopeRef.current === persistFilterScope) return;
    lastAppliedScopeRef.current = persistFilterScope;
    applyScopedFiltersToState(persistedFilters, persistFilterScope);
  }, [
    persistFilterScope,
    persistFilterKeysKey,
    persistedFilters,
    applyScopedFiltersToState,
  ]);

  useEffect(() => {
    if (!isPreferencesLoadedRef.current) return;
    if (persistFilterKeysList.length === 0) return;
    // Without an org scope, do not overwrite the org-keyed map with a flat payload
    if (!persistFilterScope) return;

    const scopedSlice = pickPersistFilters(filters, persistFilterKeysList);
    setPersistedFilters((prev) => {
      const base = prev && typeof prev === "object" ? prev : {};
      const previousSlice = base[persistFilterScope];
      if (
        previousSlice &&
        JSON.stringify(previousSlice) === JSON.stringify(scopedSlice)
      ) {
        return prev;
      }
      return {
        ...base,
        [persistFilterScope]: scopedSlice,
      };
    });
  }, [filtersKey, persistFilterKeysKey, persistFilterScope]);

  useEffect(() => {
    if (!isColumnSelection || !columnPreferenceKey) return;
    if (!isPreferencesLoadedRef.current) return;
    if (persistFilterKeysList.length > 0 && persistedFilters === undefined) {
      return;
    }
    const payload = {
      columnOrder,
      visibleColumns,
      ...(persistFilterKeysList.length > 0
        ? { filters: persistedFilters || {} }
        : {}),
    };
    const snapshot = JSON.stringify(payload);

    if (lastSyncedPreferencesRef.current === snapshot) return;

    const timeout = setTimeout(async () => {
      try {
        await AxiosInstance().put(`/table-preferences/${columnPreferenceKey}`, {
          tableName: preferenceApiTableName,
          ...payload,
        });
        lastSyncedPreferencesRef.current = snapshot;
      } catch (error) {
        // optional persistence endpoint
      }
    }, 400);
    return () => clearTimeout(timeout);
  }, [
    columnOrder,
    visibleColumns,
    persistedFilters,
    isColumnSelection,
    columnPreferenceKey,
    preferenceApiTableName,
    persistFilterKeysKey,
  ]);

  const handleDownload = async () => {
    if (onDownload) {
      const visibleColumnIds = columnOrder.filter((id) => !!visibleColumns[id]);
      await onDownload({
        search: debouncedInput,
        visibleColumnIds,
        columnOrder,
        visibleColumns,
        sort:
          sort?.sortType !== ""
            ? {
                sortKey:
                  sort.sortKeyMain !== "" ? sort.sortKeyMain : sort.sortKey,
                sortType: sort.sortType,
              }
            : null,
      });
      return;
    }
    const exportData = (
      viewData && viewData?.length > 0 ? viewData : data || []
    ).map((row: any) => {
      const exported: Record<string, any> = {};
      columnHeaders.forEach((header) => {
        if (visibleColumns[header.id]) {
          let value = row[header.id];
          if (Array.isArray(value)) {
            value = value.map((item: any) => item?.name || item).join(", ");
          }
          exported[header.label] = value ?? "";
        }
      });
      return exported;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, heading);
    XLSX.writeFile(
      wb,
      `${heading?.toLowerCase().replace(/\s+/g, "_") || "data"}.xlsx`,
    );
  };

  // console.log("filtersOptions", filtersOptions);
  // console.log("filters", filters);

  return (
    <Card className="gap-2 border-0 shadow-none rounded-none p-0 ">
      <CardHeader className="p-0">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between w-full min-w-0">
          {isIncludeSearch && (
            <div
              className={clsx(
                "relative w-full md:basis-1/2 md:w-64",
                searchWrapperClassName,
              )}
            >
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={searchPlaceholder}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="pl-9 pr-9 ignore"
              />
              {input && (
                <button
                  type="button"
                  onClick={() => setInput("")}
                  className="absolute right-3 top-3 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
          <div
            className={clsx(
              "flex flex-wrap items-center gap-2 mb-4 md:mb-0",
              isIncludeSearch && "md:ml-auto md:justify-end",
              !isIncludeSearch && "w-full justify-end",
            )}
          >
            {headerExtraControls}
            {filtersOptions.length > 0 && (
              <FilterDropdown
                filters={filters}
                filtersOptions={filtersOptions}
                setFilters={setFilters}
                defaultFilters={defaultFilters}
                onClearAllFilters={onClearAllFilters}
                isHideClearAllFilters={isHideClearAllFilters}
              />
            )}
            {isColumnSelection && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-fit px-4 py-1 justify-start text-left font-medium rounded-full teachers bg-primary/10 hover:bg-primary"
                  >
                    <span>Column View</span>
                    <ChevronDown className="ml-0 md:ml-2" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[200px] p-0">
                  <div className="p-4 max-h-[300px] overflow-y-auto">
                    {columnHeaders.map((header) => (
                      <div
                        key={header.id}
                        className="flex items-center space-x-2 py-1"
                      >
                        <Checkbox
                          id={`col-${header.id}`}
                          checked={visibleColumns[header.id] || false}
                          onCheckedChange={(checked) => {
                            setVisibleColumns((prev) => ({
                              ...prev,
                              [header.id]: !!checked,
                            }));
                          }}
                        />
                        <Label htmlFor={`col-${header.id}`} className="text-sm">
                          {header.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            )}
            {headerActions}
            {canDownload && (
              <Button onClick={handleDownload}>
                <Download /> Data
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className=" p-0">
        <div
          className={clsx(
            "flex   gap-4",
            isSelectable && selectActions.length > 0 && hasBulkSelection
              ? "justify-between"
              : "justify-end",
          )}
        >
          {isSelectable && selectActions.length > 0 && hasBulkSelection && (
            <div className="flex gap-2 items-center h-fit">
              {selectActions.map((action) => (
                <>
                  {!action?.isMulti ? (
                    <Select
                      value=""
                      onValueChange={(v) =>
                        handleSearchActionsClicked(
                          getBulkSelectionPayload(),
                          action.key,
                          v,
                          clearBulkSelection,
                        )
                      }
                    >
                      <SelectTrigger className="h-9 pr-0 w-40  bg-[#E8E9F3] border border-[#CFD2EB] teachers  text-black rounded-full px-3 py-1 text-sm [>svg]:text-black">
                        <SelectValue placeholder={action.label} />
                      </SelectTrigger>

                      <SelectContent className="">
                        {action.values.map((v: any) => (
                          <SelectItem key={v.key} value={v.key}>
                            {v.label}
                          </SelectItem>
                        ))}
                        {/* <SelectItem value="">All</SelectItem> */}
                      </SelectContent>
                    </Select>
                  ) : (
                    <AssignMultiSelect
                      placeholder={action.label}
                      options={action?.values || []}
                      handleSelectionDone={(v: any) =>
                        handleSearchActionsClicked(
                          getBulkSelectionPayload(),
                          action.key,
                          v,
                          clearBulkSelection,
                        )
                      }
                    />
                  )}
                </>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4 rounded-md border border-grey pb-2">
          {(() => {
            if (universalLoadingSkeleton) {
              const effectiveLoading =
                (!donotFetch || useCustomFetch) && loading;
              const showSkeleton = !isPreferencesReady || effectiveLoading;
              const visibleHeaderCount = columnHeaders.filter(
                (h) => visibleColumns[h.id],
              ).length;
              const skeletonColumns = Math.max(
                1,
                visibleHeaderCount || columnHeaders.length || 1,
              );
              const skeletonRows = limit || 10;
              const skeletonShowActions =
                !!isActionColumn ||
                (Array.isArray(moreActions) && moreActions.length > 0);

              if (showSkeleton) {
                return (
                  <div className="p-0">
                    <TableSkeleton
                      rows={skeletonRows}
                      columns={skeletonColumns}
                      showCheckbox={isSelectable}
                      showActions={skeletonShowActions}
                    />
                  </div>
                );
              }

              return (
                <MyTable
                  className=" min-w-0"
                  columnHeaders={columnHeaders.filter(
                    (h) => visibleColumns[h.id],
                  )}
                  columnOrder={columnOrder}
                  onColumnOrderChange={setColumnOrder}
                  loading={false}
                  limit={limit}
                  columnsData={displayCols}
                  viewColumnData={displayViews}
                  isActionColumn={isActionColumn}
                  actionFn={handleAction}
                  editActionFn={handleEditAction}
                  sort={sort}
                  sortFn={handleSort}
                  isSelectable={isSelectable}
                  selected={selected}
                  setSelected={setSelected}
                  selectAll={selectAll}
                  setSelectAll={setSelectAll}
                  unselectedPatientIds={unselectedPatientIds}
                  setUnselectedPatientIds={setUnselectedPatientIds}
                  clickRow={clickRow}
                  moreActions={moreActions}
                  clickCell={clickCell}
                  isActionDisabled={isActionDisabled}
                  isActionsHidden={isActionsHidden}
                  // paginationFn={handlePagination}
                />
              );
            }

            if (isPreferencesReady) {
              return (
                <MyTable
                  className=" min-w-0"
                  columnHeaders={columnHeaders.filter(
                    (h) => visibleColumns[h.id],
                  )}
                  columnOrder={columnOrder}
                  onColumnOrderChange={setColumnOrder}
                  loading={donotFetch ? false : loading}
                  limit={limit}
                  columnsData={displayCols}
                  viewColumnData={displayViews}
                  isActionColumn={isActionColumn}
                  actionFn={handleAction}
                  editActionFn={handleEditAction}
                  sortFn={handleSort}
                  isSelectable={isSelectable}
                  selected={selected}
                  setSelected={setSelected}
                  selectAll={selectAll}
                  setSelectAll={setSelectAll}
                  unselectedPatientIds={unselectedPatientIds}
                  setUnselectedPatientIds={setUnselectedPatientIds}
                  clickRow={clickRow}
                  moreActions={moreActions}
                  clickCell={clickCell}
                  isActionDisabled={isActionDisabled}
                  isActionsHidden={isActionsHidden}
                  // paginationFn={handlePagination}
                />
              );
            }

            return (
              <div className="p-0">
                <TableSkeleton rows={10} columns={6} />
              </div>
            );
          })()}
          {/* {totalPages} {page} */}
          <div className="px-2">
            <Pagination
              page={page}
              limit={limit}
              setLimit={setLimit}
              setPage={setPage}
              total={totalPages}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TableWithSearch;

export const caretakerTableHeaders = [
  // { id: "_id", label: "ID", isSortable: true },
  { id: "firstName", label: "Name", isSortable: true },
  // { id: "lastName", label: "Last Name", isSortable: true },
  // { id: "middleName", label: "Middle Name", isSortable: true },
  { id: "email", label: "Email", isSortable: true },
  { id: "phone", label: "Phone", isSortable: true },
  { id: "status", label: "Status", isSortable: true },
  { id: "gender", label: "Gender", isSortable: true },
  {
    id: "assignedOrganizations",
    label: "Assigned Organizations",
    isSortable: false,
  },
  // { id: "createdBy", label: "Created By", isSortable: true },
  { id: "createdAt", label: "Created At", isSortable: true },
  { id: "updatedAt", label: "Updated At", isSortable: true },
  // { id: "__v", label: "Version", isSortable: false },
  { id: "dateOfBirth", label: "Date of Birth", isSortable: true },
];
